<div class="w-full flex flex-col flex-1 min-h-0">
  <div class="mb-6 flex items-center gap-3">
    <a routerLink="/reconciliations" class="inline-flex items-center text-slate-600 hover:text-blue-600">
      <mat-icon>arrow_back</mat-icon>
      <span class="ml-1 text-sm font-medium">Volver al listado</span>
    </a>
  </div>

  @if (loading()) {
    <div class="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <p-skeleton width="40%" height="2rem" />
      <p-skeleton width="100%" height="6rem" />
      <p-skeleton width="100%" height="12rem" />
    </div>
  } @else if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-xl p-6 text-red-800">{{ error() }}</div>
  } @else if (reconciliation(); as r) {
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div class="px-6 py-5 border-b border-slate-200 bg-slate-50/50 flex flex-wrap items-center justify-between gap-4">
        <div class="min-w-0 flex-1">
          <h1 class="text-2xl font-bold text-slate-900">Liquidación #{{ r.id }}</h1>
          <p class="text-sm text-slate-500 mt-1">Proveedor: {{ r.paymentProvider }} · Externo: {{ r.externalReconciliationId }}</p>
        </div>
        <span
          class="inline-flex shrink-0"
          pTooltip="Descargar Comprobante Electrónico de Pago"
          tooltipPosition="bottom"
          appendTo="body">
          <p-button
            label="Ver CEP"
            icon="pi pi-external-link"
            iconPos="left"
            type="button"
            severity="info"
            [raised]="true"
            styleClass="!font-semibold !shadow-md hover:!shadow-lg !transition-shadow"
            [disabled]="!r.transferUrlCep?.trim()"
            (onClick)="openCep(r.transferUrlCep)" />
        </span>
      </div>

      <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-slate-100">
        <div class="space-y-3">
          <h2 class="text-sm font-semibold text-slate-700 uppercase tracking-wide">Resumen</h2>
          <dl class="grid grid-cols-1 gap-2 text-sm">
            <div class="flex justify-between gap-4 border-b border-slate-100 pb-2">
              <dt class="text-slate-500">Fecha negocio</dt>
              <dd class="font-medium text-slate-900">{{ r.businessDate || '—' }}</dd>
            </div>
            <div class="flex justify-between gap-4 border-b border-slate-100 pb-2">
              <dt class="text-slate-500">Tipo</dt>
              <dd class="text-slate-800">{{ r.reconciliationType || '—' }}</dd>
            </div>
            <div class="flex justify-between gap-4 border-b border-slate-100 pb-2">
              <dt class="text-slate-500">Subtotal</dt>
              <dd class="text-right">{{ r.subTotal != null ? (r.subTotal | number : '1.2-2') : '—' }}</dd>
            </div>
            <div class="flex justify-between gap-4 pb-2">
              <dt class="text-slate-500">Total</dt>
              <dd class="text-right font-semibold text-lg text-slate-900">
                {{ r.totalAmount != null ? (r.totalAmount | number : '1.2-2') : '—' }}
              </dd>
            </div>
          </dl>
        </div>
        <div class="space-y-3">
          <h2 class="text-sm font-semibold text-slate-700 uppercase tracking-wide">Transferencia (si aplica)</h2>
          <dl class="grid grid-cols-1 gap-2 text-sm text-slate-700">
            <div><span class="text-slate-500">Banco:</span> {{ r.transferBank || '—' }}</div>
            <div><span class="text-slate-500">CLABE:</span> {{ r.transferClabe || '—' }}</div>
            <div><span class="text-slate-500">Concepto:</span> {{ r.transferConcept || '—' }}</div>
            <div><span class="text-slate-500">Beneficiario:</span> {{ r.transferBeneficiaryName || '—' }}</div>
          </dl>
        </div>
      </div>

      <div class="p-6 overflow-x-auto">
        <h2 class="text-lg font-semibold text-slate-900 mb-4">Movimientos</h2>
        @if (r.lines?.length) {
          @let totals = lineTotals(r.lines);
          <p-table
            [value]="r.lines"
            scrollable="true"
            scrollHeight="420px"
            [tableStyle]="{ 'min-width': '64rem' }"
            styleClass="reconciliation-lines-table p-datatable-sm p-datatable-striped">
            <ng-template pTemplate="header">
              <tr>
                <th class="!px-2">Venta</th>
                <th class="!px-2">Recibo</th>
                <th class="!px-2">Cliente</th>
                <th class="!px-2">ID</th>
                <th class="!px-2">Referencia</th>
                <th class="!px-2">Tarjeta</th>
                <th class="!px-2">Emisor</th>
                <th class="!px-2">Marca</th>
                <th class="!px-2">Estado</th>
                <th class="!px-2">Fecha txn</th>
                <th class="!px-2 text-right">Monto</th>
                <th class="!px-2 text-right">Tot. com.</th>
                <th class="!px-2 text-right">Depósito</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-line>
              <tr>
                <td class="!px-2 max-w-[8rem]">
                  @if (line.sale?.id) {
                    <button
                      type="button"
                      class="text-left text-sm font-medium text-violet-700 hover:underline truncate max-w-full"
                      (click)="openSaleDetails(line.sale.id, $event)">
                      {{ saleLabel(line) }}
                    </button>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>
                <td class="!px-2 max-w-[8rem] whitespace-nowrap">
                  @if (line.charge?.id) {
                    <button
                      type="button"
                      class="text-left text-sm font-medium text-sky-700 hover:underline"
                      (click)="openChargeDetails(line.charge, $event)">
                      {{ chargeLabel(line) }}
                    </button>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>
                <td class="!px-2 max-w-[10rem]">
                  @if (line.customer?.id) {
                    <button
                      type="button"
                      class="text-left text-sm text-slate-800 hover:text-sky-700 hover:underline truncate max-w-full"
                      [title]="customerLabel(line)"
                      (click)="openCustomerDetails(line.customer.id, $event)">
                      {{ customerLabel(line) }}
                    </button>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>
                <td class="!px-2 whitespace-nowrap">{{ line.id ?? '—' }}</td>
                <td class="!px-2 max-w-[7rem]"><span class="break-all">{{ line.reference || '—' }}</span></td>
                <td class="!px-2 whitespace-nowrap">{{ line.cardNumberMasked || '—' }}</td>
                <td class="!px-2 max-w-[6rem]"><span class="break-all">{{ line.issuer || '—' }}</span></td>
                <td class="!px-2">{{ line.cardBrand || '—' }}</td>
                <td class="!px-2">{{ line.status || '—' }}</td>
                <td class="!px-2 whitespace-nowrap text-xs">{{ transactionDateOnly(line.transactionAt) }}</td>
                <td class="!px-2 text-right whitespace-nowrap">{{ line.amount != null ? (line.amount | number : '1.2-2') : '—' }}</td>
                <td class="!px-2 text-right whitespace-nowrap">{{ line.totalCommission != null ? (line.totalCommission | number : '1.2-2') : '—' }}</td>
                <td class="!px-2 text-right font-medium whitespace-nowrap">{{ line.lineTotal != null ? (line.lineTotal | number : '1.2-2') : '—' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="!bg-slate-100 font-semibold border-t-2 border-slate-300">
                <td colspan="10" class="!px-2 !py-2.5 text-right text-slate-800">Totales</td>
                <td class="!px-2 !py-2.5 text-xs text-right whitespace-nowrap">{{ totals.amount | number : '1.2-2' }}</td>
                <td class="!px-2 !py-2.5 text-xs text-right whitespace-nowrap">{{ totals.totalCommission | number : '1.2-2' }}</td>
                <td class="!px-2 !py-2.5 text-xs font-bold text-right whitespace-nowrap text-slate-900">{{ totals.lineTotal | number : '1.2-2' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="13" class="!text-center !text-slate-500">Sin movimientos</td>
              </tr>
            </ng-template>
          </p-table>
        } @else {
          <p class="text-slate-500 text-sm">No hay líneas de movimiento para esta liquidación.</p>
        }
      </div>
    </div>
  }

  <app-charge-read
    [charge]="selectedCharge()"
    [visible]="isChargeDetailsOpen()"
    (visibleChange)="onChargeDrawerVisibleChange($event)" />

  <p-drawer
    [visible]="isSaleDetailsOpen()"
    position="right"
    (visibleChange)="onSaleDrawerVisibleChange($event)"
    styleClass="sale-details-drawer !w-[800px]"
    [blockScroll]="true">
    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 shadow-sm">
            <i class="pi pi-shopping-cart text-base"></i>
          </span>
          <h3 class="text-lg font-semibold text-slate-900">Detalle de venta</h3>
        </div>
        @if (selectedSale()?.id; as saleId) {
          @if (!saleDetailsLoading()) {
            <a
              [routerLink]="['/sales', saleId]"
              class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0">
              <i class="pi pi-external-link text-sm"></i>
              Ver venta
            </a>
          }
        }
      </div>
    </ng-template>
    @if (saleDetailsLoading()) {
      <div class="flex flex-col items-center justify-center py-12 gap-4">
        <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
        <p class="text-sm text-slate-600">Cargando detalle...</p>
      </div>
    } @else if (selectedSale()) {
      <app-sale-read
        [sale]="selectedSale()!"
        [charges]="saleCharges()"
        (openCustomer)="openCustomerDetails($event)" />
    }
  </p-drawer>

  <p-drawer
    [visible]="isCustomerDetailsOpen()"
    position="right"
    (visibleChange)="onCustomerDrawerVisibleChange($event)"
    styleClass="customer-details-drawer !w-[800px]"
    [blockScroll]="true">
    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600 shadow-sm">
            <i class="pi pi-user text-base"></i>
          </span>
          <h3 class="text-lg font-semibold text-slate-900">Detalles del cliente</h3>
        </div>
        @if (selectedCustomer()?.id; as customerId) {
          @if (!customerDetailsLoading()) {
            <a
              [routerLink]="['/customers', customerId]"
              class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0">
              <i class="pi pi-external-link text-sm"></i>
              Ver cliente
            </a>
          }
        }
      </div>
    </ng-template>
    @if (customerDetailsLoading()) {
      <div class="flex flex-col items-center justify-center py-12 gap-4">
        <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
        <p class="text-sm text-slate-600">Cargando cliente...</p>
      </div>
    } @else if (selectedCustomer()) {
      <app-customer-read
        [customer]="selectedCustomer()!"
        (openSale)="openSaleDetailsFromCustomer($event)" />
    }
  </p-drawer>
</div>
